/**
 * App automations (ADR-0010) — the list of an app's **workflows** (kind `app`,
 * bound to `app_id`), reacting to `row_changed` / `form_submitted` triggers over
 * the app's data. Self-contained section (extracted from the standalone
 * app-detail page) so it can be mounted BOTH there and as a single-pane area in
 * the page-builder editor (ADR-0048 single-IDE).
 *
 * Authoring/editing happens in the WORKFLOW plane editor (`/workflows/:id`) —
 * deliberately decoupled from the apps plane (see `planes-separation`), so this
 * section is a LIST + create that opens that editor; it does not embed it.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Zap } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { SectionHeader } from "@/components/apps/SectionHeader";
import { workflowsApi } from "@/lib/api-workflows";

export function AppAutomationsSection({ appId, canWrite }: { appId: string; canWrite: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const wfQ = useQuery({
    queryKey: ["workflows", "app", appId],
    queryFn: () => workflowsApi.list({ kind: "app", app_id: appId, page_size: 100 }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => workflowsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workflows", "app", appId] }),
  });

  const items = wfQ.data?.items ?? [];

  return (
    <section className="space-y-2">
      <SectionHeader
        icon={Zap}
        title={t("apps.automations", "Automations")}
        count={items.length}
        action={
          canWrite ? (
            <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("apps.newAutomation", "New automation")}
            </Button>
          ) : undefined
        }
      />
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("apps.noAutomations", "No automations yet — react to row/form events with a workflow.")}
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border/60">
          <Table>
            <Thead>
              <Tr>
                <Th>{t("apps.automationName", "Automation")}</Th>
                <Th className="w-20 text-center">{t("apps.status", "Status")}</Th>
                {canWrite && <Th className="w-16 text-right">{t("common.actions", "Actions")}</Th>}
              </Tr>
            </Thead>
            <Tbody>
              {items.map((wf) => (
                <Tr
                  key={wf.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/workflows/${wf.id}`)}
                >
                  <Td className="font-medium">{wf.name}</Td>
                  <Td className="text-center">
                    <Badge tone={wf.enabled ? "active" : "neutral"}>
                      {wf.enabled ? t("apps.enabled", "on") : t("apps.disabled", "off")}
                    </Badge>
                  </Td>
                  {canWrite && (
                    <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t("common.delete", "Delete")}
                        onClick={() => {
                          if (confirm(t("apps.confirmDeleteAutomation", 'Delete automation "{{name}}"?', { name: wf.name }))) {
                            remove.mutate(wf.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                      </Button>
                    </Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      )}
      <CreateAutomationDialog appId={appId} open={open} onOpenChange={setOpen} />
    </section>
  );
}

function CreateAutomationDialog({
  appId,
  open,
  onOpenChange,
}: {
  appId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = React.useState("");

  React.useEffect(() => {
    if (open) setName("");
  }, [open]);

  const create = useMutation({
    mutationFn: () => workflowsApi.create({ name: name.trim(), kind: "app", app_id: appId }),
    onSuccess: (wf) => {
      qc.invalidateQueries({ queryKey: ["workflows", "app", appId] });
      onOpenChange(false);
      navigate(`/workflows/${wf.id}`);
    },
  });

  const canSubmit = name.trim().length > 0 && !create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("apps.newAutomation", "New automation")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="auto-name">{t("apps.automationName", "Automation")}</Label>
            <Input
              id="auto-name"
              value={name}
              autoFocus
              placeholder="On new ticket"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <p className="text-[11px] text-muted-foreground">
            {t("apps.automationHint", "An app workflow — start it from a row_changed / form_submitted trigger over this app's data.")}
          </p>
          {create.isError && (
            <Alert tone="danger" title={t("apps.createAutomationFailed", "Could not create automation")}>
              <span className="font-mono text-xs">{(create.error as Error).message}</span>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={!canSubmit} onClick={() => create.mutate()}>
            {t("apps.create", "Create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
